import { useState, useCallback, useEffect } from 'react';
import { formApi } from '@/api/raynetClient';
import { DealerSurveyData } from '@/api/types';
import { useAuth } from '@/contexts/AuthContext';

interface FormPersistenceState {
  formUuid: string | null;
  isSaving: boolean;
  isLoading: boolean;
  lastSaved: Date | null;
  error: string | null;
}

interface SavedForm {
  uuid: string;
  company_name: string;
  contact_person: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  step_data: DealerSurveyData;
  visit_date: string | null;
  representative: string | null;
}

export const useFormPersistence = (disableAutoSave = false) => {
  const { user } = useAuth();
  const [state, setState] = useState<FormPersistenceState>({
    formUuid: null,
    isSaving: false,
    isLoading: false,
    lastSaved: null,
    error: null,
  });

  // Save form (create new or update existing)
  const saveForm = useCallback(async (
    formData: DealerSurveyData,
    status: string = 'draft'
  ): Promise<string | null> => {
    setState(prev => ({ ...prev, isSaving: true, error: null }));

    try {
      let uuid = state.formUuid;

      const saveData = {
        companyName: formData.companyName,
        contactPerson: formData.contactName,
        email: formData.contactEmail,
        phone: formData.contactPhone,
        visitDate: formData.visitDate,
        representative: formData.salesRep, // Now salesRep contains username/email
        stepData: formData,
        status,
      };

      if (uuid) {
        // Update existing form
        await formApi.updateForm(uuid, {
          stepData: formData,
          status,
        });
        console.log('✅ Form updated:', uuid);
      } else {
        // Create new form
        const result = await formApi.saveForm(saveData);
        uuid = result.uuid;
        console.log('✅ New form created:', uuid);
      }

      setState(prev => ({
        ...prev,
        formUuid: uuid,
        isSaving: false,
        lastSaved: new Date(),
        error: null,
      }));

      return uuid;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Chyba při ukládání formuláře';
      console.error('❌ Save form error:', errorMessage);
      
      setState(prev => ({
        ...prev,
        isSaving: false,
        error: errorMessage,
      }));

      return null;
    }
  }, [state.formUuid, user]);

  // Load form by UUID
  const loadForm = useCallback(async (uuid: string): Promise<DealerSurveyData | null> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const form: SavedForm = await formApi.loadForm(uuid);
      
      setState(prev => ({
        ...prev,
        formUuid: uuid,
        isLoading: false,
        error: null,
      }));

      console.log('✅ Form loaded:', uuid);
      return form.step_data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Chyba při načítání formuláře';
      console.error('❌ Load form error:', errorMessage);
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));

      return null;
    }
  }, []);

  // Load complete form with status by UUID  
  const loadCompleteForm = useCallback(async (uuid: string): Promise<SavedForm | null> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const form: SavedForm = await formApi.loadForm(uuid);
      
      setState(prev => ({
        ...prev,
        formUuid: uuid,
        isLoading: false,
        error: null,
      }));
      console.log('✅ Complete form loaded:', uuid, 'status:', form.status);
      return form;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Chyba při načítání kompletního formuláře';
      console.error('❌ Load complete form error:', errorMessage);
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));

      return null;
    }
  }, []);

  // Get list of forms for current user
  const listForms = useCallback(async (representative?: string): Promise<SavedForm[]> => {
    try {
      const targetRepresentative = representative || user?.username;
      
      if (!targetRepresentative) {
        console.warn('⚠️ useFormPersistence: Není dostupný username pro filtraci formulářů');
        return [];
      }

      console.log('📋 useFormPersistence: Načítání formulářů pro reprezentanta:', targetRepresentative);
      console.log('📋 useFormPersistence: User context:', {
        username: user?.username,
        full_name: user?.full_name,
        id: user?.id
      });
      
      const forms = await formApi.listForms({ 
        representative: targetRepresentative,
        limit: 50 
      });
      
      console.log('✅ useFormPersistence: API vrátilo formulářů:', forms.length, 'pro reprezentanta:', targetRepresentative);
      forms.forEach((form: SavedForm, index: number) => {
        console.log(`📋 useFormPersistence: Form ${index + 1}:`, {
          uuid: form.uuid.slice(0, 8),
          company: form.company_name,
          representative: form.representative,
          status: form.status
        });
      });
      
      return forms;
    } catch (error) {
      console.error('❌ List forms error:', error);
      return [];
    }
  }, [user?.username]);

  // Auto-save with debouncing
  const autoSave = useCallback(
    async (formData: DealerSurveyData, delay: number = 2000) => {
      // Don't auto-save if disabled or if company name is empty
      if (disableAutoSave || !formData.companyName?.trim()) {
        return () => {}; // Return empty cleanup function
      }

      // Clear existing timeout if any
      const timeoutId = setTimeout(async () => {
        await saveForm(formData, 'draft');
      }, delay);

      // Cleanup function
      return () => clearTimeout(timeoutId);
    },
    [saveForm, disableAutoSave]
  );

  // Mark form as submitted
  const markAsSubmitted = useCallback(async (
    formData: DealerSurveyData,
    raynetPersonId?: number,
    raynetLeadId?: number
  ): Promise<boolean> => {
    if (!state.formUuid) {
      console.error('No form UUID available for submission');
      return false;
    }

    setState(prev => ({ ...prev, isSaving: true, error: null }));

    try {
      await formApi.updateForm(state.formUuid, {
        finalSubmission: formData,
        status: 'submitted',
        raynetPersonId,
        raynetLeadId,
      });

      setState(prev => ({
        ...prev,
        isSaving: false,
        lastSaved: new Date(),
        error: null,
      }));

      console.log('✅ Form marked as submitted:', state.formUuid);
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Chyba při označení formuláře jako odeslaný';
      console.error('❌ Mark submitted error:', errorMessage);
      
      setState(prev => ({
        ...prev,
        isSaving: false,
        error: errorMessage,
      }));

      return false;
    }
  }, [state.formUuid]);

  // Clear current form
  const clearForm = useCallback(() => {
    setState({
      formUuid: null,
      isSaving: false,
      isLoading: false,
      lastSaved: null,
      error: null,
    });
  }, []);

  // Delete form
  const deleteForm = useCallback(async (uuid: string): Promise<boolean> => {
    try {
      await formApi.deleteForm(uuid);
      
      // If deleted form is currently loaded, clear state
      if (state.formUuid === uuid) {
        clearForm();
      }

      console.log('✅ Form deleted:', uuid);
      return true;
    } catch (error) {
      console.error('❌ Delete form error:', error);
      return false;
    }
  }, [state.formUuid, clearForm]);

  // Auto-clear error after 5 seconds
  useEffect(() => {
    if (state.error) {
      const timeout = setTimeout(() => {
        setState(prev => ({ ...prev, error: null }));
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [state.error]);

  return {
    // State
    formUuid: state.formUuid,
    isSaving: state.isSaving,
    isLoading: state.isLoading,
    lastSaved: state.lastSaved,
    error: state.error,
    isFormSaved: !!state.formUuid,

    // Actions
    saveForm,
    loadForm,
    loadCompleteForm,
    listForms,
    autoSave,
    markAsSubmitted,
    clearForm,
    deleteForm,
  };
};

export type { FormPersistenceState, SavedForm }; 